import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { updateMe } from "../../api/me";
import type { Profile, Transport } from "../../api/me";
import { errorText } from "../../api/client";
import { Button } from "../../components/Button";
import { FormError } from "../../components/Field";
import { nl } from "../../i18n/nl";
import { AuthLayout } from "../auth/AuthLayout";
import { NEXT_PARAM, safeNext } from "../auth/RequireAuth";
import { ME_KEY, useMe } from "../auth/session";
import authStyles from "../auth/auth.module.css";
import { DistancePicker, MunicipalityPicker, NotifySwitches, ProfilePicker, TransportPicker } from "./PreferenceFields";
import type { NotifyKey } from "./PreferenceFields";
import styles from "./profile.module.css";

const t = nl.onboarding;
const STEPS = 3;

/** First visit after registering: profile(s), start municipality / distance / transport, notifications. */
export function OnboardingPage() {
  const me = useMe();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [profiles, setProfiles] = useState<Profile[]>(me.data?.profiles ?? []);
  const [municipality, setMunicipality] = useState<string | null>(me.data?.home_municipality ?? null);
  const [distance, setDistance] = useState<number | null>(me.data?.max_distance_km ?? 25);
  const [transport, setTransport] = useState<Transport | null>(me.data?.transport ?? null);
  const [notify, setNotify] = useState<Record<NotifyKey, boolean>>({
    claim: true,
    wait: true,
    follow: true,
    digest: true,
    email: false,
    ...me.data?.notify,
  });
  const [profileError, setProfileError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Move focus to the new step's heading (keyboard and screen reader users).
  useEffect(() => {
    if (step > 0) formRef.current?.querySelector<HTMLElement>("h1")?.focus();
  }, [step]);

  const save = useMutation({
    mutationFn: () =>
      updateMe({
        profiles,
        home_municipality: municipality,
        max_distance_km: distance,
        transport,
        notify,
      }),
    onSuccess: (updated) => {
      client.setQueryData(ME_KEY, updated);
      void navigate(safeNext(params.get(NEXT_PARAM)), { replace: true });
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (step === 0 && profiles.length === 0) {
      setProfileError(nl.prefs.profilesRequired);
      return;
    }
    setProfileError(null);
    if (step < STEPS - 1) setStep(step + 1);
    else save.mutate();
  }

  return (
    <AuthLayout>
      <form ref={formRef} className={styles.onboarding} onSubmit={submit} noValidate>
        <p className={authStyles.kicker}>{t.stepOf(step + 1, STEPS)}</p>
        <h1 className={authStyles.title} tabIndex={-1}>
          {t.titles[step]}
        </h1>
        {step === 0 && <p className={authStyles.lead}>{t.lead}</p>}
        <div className={styles.stepBody}>
          {step === 0 && (
            <ProfilePicker
              value={profiles}
              onChange={(next) => {
                setProfiles(next);
                if (next.length > 0) setProfileError(null);
              }}
              error={profileError}
            />
          )}
          {step === 1 && (
            <>
              <MunicipalityPicker value={municipality} onChange={setMunicipality} />
              <DistancePicker value={distance} onChange={setDistance} />
              <TransportPicker value={transport} onChange={setTransport} />
            </>
          )}
          {step === 2 && <NotifySwitches value={notify} onChange={setNotify} />}
        </div>
        <FormError message={save.error ? errorText(save.error) : null} />
        <div className={styles.stepActions}>
          {step > 0 && (
            <Button
              variant="ghost"
              onClick={() => {
                setStep(step - 1);
              }}
            >
              {t.back}
            </Button>
          )}
          <Button type="submit" variant="primary" size="large" disabled={save.isPending}>
            {save.isPending ? t.saving : step < STEPS - 1 ? t.next : t.finish}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
